import { ChangeDetectionStrategy, Component, computed, DestroyRef, input, OnInit, output } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, finalize, of, take } from 'rxjs';
import { hexToRgba } from 'src/app/common/colorUtils';
import { findingAttackToDisplayName } from 'src/app/common/findingRulesUtils';
import {
  anyOwaspToDisplayName,
  getviolationRuleRiskReasonCardFieldKey,
  getViolationRuleToOwaspLinks,
} from 'src/app/common/violationRulesUtils';
import { ISecurityTag, SecurityTagComponent } from 'src/app/components/security-tag/security-tag.component';
import { ITimelineItem, TimelineComponent } from 'src/app/components/timeline/timeline.component';
import { IntegrationModel } from 'src/app/models/api';
import { LabelModel } from 'src/app/models/api/common';
import { FindingModel } from 'src/app/models/api/finding.model';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CopyButtonComponent } from 'src/app/components/copy-button/copy-button.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NoDataComponent } from 'src/app/components/no-data/no-data.component';
import { LucideAngularModule } from 'lucide-angular';
import { EvidenceComponent } from '../evidence-list/evidence.component';

@Component({
  selector: 'app-finding-item',
  standalone: true,
  templateUrl: './finding-item.component.html',
  styleUrls: ['./finding-item.component.scss'],
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    CopyButtonComponent,
    MatTooltipModule,
    LabelsProviderPipe,
    NoDataComponent,
    SecurityTagComponent,
    TimelineComponent,
    LucideAngularModule,
    EvidenceComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindingItemComponent implements OnInit {
  finding = input.required<FindingModel>();
  evidenceStepClicked = output<MouseEvent>();

  public isLoading$ = new BehaviorSubject<boolean>(true);
  public findingModel: FindingModel;
  public integrationName: string;
  public showNoData = false;
  public owaspRiskTag: ISecurityTag[] = [];
  public attackRiskTag: ISecurityTag[] = [];
  public triageSuggestion: ITimelineItem[] = [];
  public fixSuggestion: ITimelineItem[] = [];
  private storeLabels = toSignal(
    this._storeService.labels$,
    { initialValue: [] as LabelModel[] }
  );

  labelChips = computed(() => {
    const colorMap = new Map<string, string>();
    for (const l of this.storeLabels()) {
      colorMap.set(l.id, l.color);
    }
    const bindings = this.finding().labelBindings ?? [];
    return bindings
      .filter((lb) => lb.label != null)
      .map((lb) => {
        const color =
          colorMap.get(lb.label.id) ?? '#6b7280';
        return {
          id: lb.id,
          displayName: lb.label.displayName,
          color,
          backgroundColor: hexToRgba(color, 0.3),
        };
      });
  });

  constructor(
    private readonly _storeService: StoreService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _destroyRef: DestroyRef
  ) {}

  public ngOnInit() {
    combineLatest([of(this.finding()), this._storeService.integrations$])
      .pipe(
        take(1),
        filter(([finding]) => {
          if (!finding) {
            console.warn('Finding not found in the server. Not showing any data');
            this.showNoData = true;
            return false;
          }
          return true;
        }),
        finalize(() => this.isLoading$.next(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: ([finding, integrations]) => {
          this.initialize(finding, integrations);
        },
        error: (error) => {
          if (error.status === 404) this.showNoData = true;
          else this._httpErrorHandlerService.handle(error);
        },
      });
  }

  private async initialize(finding: FindingModel, integrations: IntegrationModel[]): Promise<void> {
    const selectedIntegration: IntegrationModel | undefined = integrations.find(
      (integration) => integration.id === finding.integration.id
    );
    this.integrationName = selectedIntegration?.name ?? '';

    this.findingModel = finding;
    this.showNoData = false;

    this.owaspRiskTag = this.findingModel.rule.owasp.map((owasp) => ({
      key: this._labelsProviderPipe.transform(getviolationRuleRiskReasonCardFieldKey(owasp)),
      value: this._beautifyDisplayStringPipe.transform(anyOwaspToDisplayName[owasp]),
      link: getViolationRuleToOwaspLinks(owasp),
    }));

    this.attackRiskTag = this.findingModel.rule.genAiAttack.map((attack) => ({
      key: this._labelsProviderPipe.transform('violationPage.riskReasonCardAttackFieldKey'),
      value: this._beautifyDisplayStringPipe.transform(findingAttackToDisplayName[attack.toUpperCase()]),
    }));

    this.findingModel.rule.triageSuggestion
      .split(/\r?\n/)
      .forEach((element) => this.triageSuggestion.push({ text: element }));
    this.findingModel.rule.fixSuggestion
      .split(/\r?\n/)
      .forEach((element) => this.fixSuggestion.push({ text: element }));
  }
}
